import { createFileRoute } from '@tanstack/react-router';
import { BarChart3, Bell, Calendar, Globe, HelpCircle, Settings, Smartphone, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ProtectedRoute } from '@/components/auth/protected-route';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';

export const Route = createFileRoute('/help')({
  component: () => (
    <ProtectedRoute>
      <HelpPage />
    </ProtectedRoute>
  ),
});

function HelpPage() {
  const { t } = useTranslation();

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <HelpCircle className="h-8 w-8" />
        <h1 className="text-3xl font-bold">{t('help.title')}</h1>
      </div>

      <Accordion type="multiple" className="space-y-4">
        {/* Getting Started */}
        <AccordionItem value="getting-started" className="border rounded-lg px-4">
          <AccordionTrigger className="text-lg font-semibold">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              {t('help.gettingStarted.title')}
            </div>
          </AccordionTrigger>
          <AccordionContent className="space-y-4">
            <p className="text-sm text-muted-foreground">{t('help.gettingStarted.description')}</p>
            <div>
              <h4 className="font-semibold mb-2">{t('help.gettingStarted.login.title')}</h4>
              <p className="text-sm text-muted-foreground mb-2">{t('help.gettingStarted.login.description')}</p>
              <ol className="list-decimal list-inside text-sm space-y-1 ml-2">
                <li>{t('help.gettingStarted.login.step1')}</li>
                <li>{t('help.gettingStarted.login.step2')}</li>
                <li>{t('help.gettingStarted.login.step3')}</li>
              </ol>
            </div>
            <div>
              <h4 className="font-semibold mb-2">{t('help.gettingStarted.firstSteps.title')}</h4>
              <ul className="list-disc list-inside text-sm space-y-1 ml-2">
                <li>{t('help.gettingStarted.firstSteps.step1')}</li>
                <li>{t('help.gettingStarted.firstSteps.step2')}</li>
                <li>{t('help.gettingStarted.firstSteps.step3')}</li>
              </ul>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Appointments */}
        <AccordionItem value="appointments" className="border rounded-lg px-4">
          <AccordionTrigger className="text-lg font-semibold">
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              {t('help.appointments.title')}
            </div>
          </AccordionTrigger>
          <AccordionContent className="space-y-4">
            <p className="text-sm text-muted-foreground">{t('help.appointments.description')}</p>
            <div>
              <h4 className="font-semibold mb-2">{t('help.appointments.create.title')}</h4>
              <ol className="list-decimal list-inside text-sm space-y-1 ml-2">
                <li>{t('help.appointments.create.step1')}</li>
                <li>{t('help.appointments.create.step2')}</li>
                <li>{t('help.appointments.create.step3')}</li>
                <li>{t('help.appointments.create.step4')}</li>
              </ol>
            </div>
            <div>
              <h4 className="font-semibold mb-2">{t('help.appointments.claim.title')}</h4>
              <p className="text-sm text-muted-foreground mb-2">{t('help.appointments.claim.description')}</p>
              <ul className="list-disc list-inside text-sm space-y-1 ml-2">
                <li>{t('help.appointments.claim.step1')}</li>
                <li>{t('help.appointments.claim.step2')}</li>
                <li>{t('help.appointments.claim.step3')}</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-2">{t('help.appointments.statuses.title')}</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">{t('help.appointments.statuses.unclaimed')}</Badge>
                  <span className="text-sm">{t('help.appointments.statuses.unclaimedDesc')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="default">{t('help.appointments.statuses.claimed')}</Badge>
                  <span className="text-sm">{t('help.appointments.statuses.claimedDesc')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{t('help.appointments.statuses.completed')}</Badge>
                  <span className="text-sm">{t('help.appointments.statuses.completedDesc')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="destructive">{t('help.appointments.statuses.cancelled')}</Badge>
                  <span className="text-sm">{t('help.appointments.statuses.cancelledDesc')}</span>
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Calendar Integration */}
        <AccordionItem value="calendar" className="border rounded-lg px-4">
          <AccordionTrigger className="text-lg font-semibold">
            <div className="flex items-center gap-2">
              <Globe className="h-5 w-5" />
              {t('help.calendar.title')}
            </div>
          </AccordionTrigger>
          <AccordionContent className="space-y-4">
            <p className="text-sm text-muted-foreground">{t('help.calendar.description')}</p>
            <div>
              <h4 className="font-semibold mb-2">{t('help.calendar.setup.title')}</h4>
              <ol className="list-decimal list-inside text-sm space-y-1 ml-2">
                <li>{t('help.calendar.setup.step1')}</li>
                <li>{t('help.calendar.setup.step2')}</li>
                <li>{t('help.calendar.setup.step3')}</li>
                <li>{t('help.calendar.setup.step4')}</li>
              </ol>
            </div>
            <div>
              <h4 className="font-semibold mb-2">{t('help.calendar.apps.title')}</h4>
              <ul className="list-disc list-inside text-sm space-y-1 ml-2">
                <li>{t('help.calendar.apps.google')}</li>
                <li>{t('help.calendar.apps.outlook')}</li>
                <li>{t('help.calendar.apps.apple')}</li>
                <li>{t('help.calendar.apps.other')}</li>
              </ul>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Settings */}
        <AccordionItem value="settings" className="border rounded-lg px-4">
          <AccordionTrigger className="text-lg font-semibold">
            <div className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              {t('help.settings.title')}
            </div>
          </AccordionTrigger>
          <AccordionContent className="space-y-4">
            <p className="text-sm text-muted-foreground">{t('help.settings.description')}</p>
            <div>
              <h4 className="font-semibold mb-2">{t('help.settings.language.title')}</h4>
              <p className="text-sm text-muted-foreground">{t('help.settings.language.description')}</p>
            </div>
            <div>
              <h4 className="font-semibold mb-2">{t('help.settings.calendar.title')}</h4>
              <p className="text-sm text-muted-foreground mb-2">{t('help.settings.calendar.description')}</p>
              <ul className="list-disc list-inside text-sm space-y-1 ml-2">
                <li>{t('help.settings.calendar.scope')}</li>
                <li>{t('help.settings.calendar.busy')}</li>
              </ul>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Analytics */}
        <AccordionItem value="analytics" className="border rounded-lg px-4">
          <AccordionTrigger className="text-lg font-semibold">
            <div className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              {t('help.analytics.title')}
            </div>
          </AccordionTrigger>
          <AccordionContent className="space-y-4">
            <p className="text-sm text-muted-foreground">{t('help.analytics.description')}</p>
            <div>
              <h4 className="font-semibold mb-2">{t('help.analytics.charts.title')}</h4>
              <ul className="list-disc list-inside text-sm space-y-1 ml-2">
                <li>{t('help.analytics.charts.appointments')}</li>
                <li>{t('help.analytics.charts.completion')}</li>
                <li>{t('help.analytics.charts.activity')}</li>
              </ul>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Troubleshooting */}
        <AccordionItem value="troubleshooting" className="border rounded-lg px-4">
          <AccordionTrigger className="text-lg font-semibold">
            <div className="flex items-center gap-2">
              <HelpCircle className="h-5 w-5" />
              {t('help.troubleshooting.title')}
            </div>
          </AccordionTrigger>
          <AccordionContent className="space-y-4">
            <p className="text-sm text-muted-foreground">{t('help.troubleshooting.description')}</p>
            <div>
              <h4 className="font-semibold mb-2">{t('help.troubleshooting.common.title')}</h4>
              <div className="space-y-3">
                <div>
                  <p className="font-medium text-sm">{t('help.troubleshooting.common.login')}</p>
                  <p className="text-sm text-muted-foreground">{t('help.troubleshooting.common.loginSolution')}</p>
                </div>
                <div>
                  <p className="font-medium text-sm">{t('help.troubleshooting.common.calendar')}</p>
                  <p className="text-sm text-muted-foreground">{t('help.troubleshooting.common.calendarSolution')}</p>
                </div>
                <div>
                  <p className="font-medium text-sm">{t('help.troubleshooting.common.mobile')}</p>
                  <p className="text-sm text-muted-foreground">{t('help.troubleshooting.common.mobileSolution')}</p>
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Mobile Usage */}
        <AccordionItem value="mobile" className="border rounded-lg px-4">
          <AccordionTrigger className="text-lg font-semibold">
            <div className="flex items-center gap-2">
              <Smartphone className="h-5 w-5" />
              {t('help.mobile.title')}
            </div>
          </AccordionTrigger>
          <AccordionContent className="space-y-4">
            <p className="text-sm text-muted-foreground">{t('help.mobile.description')}</p>
            <div>
              <h4 className="font-semibold mb-2">{t('help.mobile.access.title')}</h4>
              <ol className="list-decimal list-inside text-sm space-y-1 ml-2">
                <li>{t('help.mobile.access.step1')}</li>
                <li>{t('help.mobile.access.step2')}</li>
                <li>{t('help.mobile.access.step3')}</li>
              </ol>
            </div>
            <div>
              <h4 className="font-semibold mb-2">{t('help.mobile.tips.title')}</h4>
              <ul className="list-disc list-inside text-sm space-y-1 ml-2">
                <li>{t('help.mobile.tips.tip1')}</li>
                <li>{t('help.mobile.tips.tip2')}</li>
                <li>{t('help.mobile.tips.tip3')}</li>
              </ul>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Contact */}
        <AccordionItem value="contact" className="border rounded-lg px-4">
          <AccordionTrigger className="text-lg font-semibold">
            <div className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              {t('help.contact.title')}
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <p className="text-sm text-muted-foreground mb-2">{t('help.contact.description')}</p>
            <p className="text-sm">{t('help.contact.content')}</p>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
